import { motion, useReducedMotion } from "framer-motion";
import { useId } from "react";

const CENTER_VARIANTS = {
  hidden: { opacity: 0, scale: 0.4 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.45, ease: "easeOut" },
  },
} as const;

const laurelVariants = (fromRotation: number) =>
  ({
    hidden: { opacity: 0, rotate: fromRotation },
    visible: {
      opacity: 1,
      rotate: 0,
      transition: { duration: 0.6, delay: 0.35, ease: "easeOut" },
    },
  }) as const;

export function PremiereShopBadge({ className, active }: { className?: string; active: boolean }) {
  const reduced = useReducedMotion();
  const uid = useId().replace(/:/g, "");

  return (
    <motion.svg
      className={className}
      fill="none"
      viewBox="0 0 96 96"
      xmlns="http://www.w3.org/2000/svg"
      initial={reduced ? false : "hidden"}
      animate={active ? "visible" : "hidden"}
    >
      <motion.g variants={laurelVariants(-35)} style={{ originX: 0.85, originY: 0.8 }}>
        <path
          d="M12.279 10.132c-1.275-.475-2.678.38-2.926 1.783l-.461 2.612c-.518 2.934-.037 5.94 1.361 8.495 1.398 2.555 3.623 4.498 6.289 5.492l.502.187a38.951 38.951 0 00-3.13 8.798c-.33-.462-.69-.9-1.081-1.314-1.974-2.084-4.589-3.354-7.372-3.582l-2.647-.218C1.5 32.279.404 33.434.507 34.822l.205 2.794c.217 2.938 1.42 5.698 3.394 7.782 1.975 2.084 4.59 3.354 7.373 3.583l1.522.124a38.399 38.399 0 001.187 7.358c-2.348-1.338-5.056-1.795-7.657-1.283l-2.744.54c-1.263.248-2.003 1.6-1.575 2.88l.93 2.778c.917 2.744 2.754 5.068 5.17 6.54 2.414 1.471 5.24 1.99 7.95 1.456l2.744-.54c.347-.068.654-.22.908-.43a32.22 32.22 0 004.278 4.677 11.242 11.242 0 00-2.46 1.69l-2.224 2.008c-.95.859-.95 2.35 0 3.21l2.224 2.008a11.243 11.243 0 0015.072 0c1.399-1.263 2.721-2.585 2.732-4.534a32.018 32.018 0 01-11.876-6.457c-4.35-3.552-7.769-8.49-9.612-14.408a32.059 32.059 0 01-1.567-9.93c0-1.949.174-3.856.506-5.708.738-4.547 2.344-8.748 4.475-12.411 2.121-3.09 1.091-9.092-.521-12.04-1.398-2.555-3.623-4.498-6.289-5.491l-2.373-.885z"
          fill={`url(#${uid}-laurel_left)`}
        />
      </motion.g>
      <motion.g variants={laurelVariants(35)} style={{ originX: 0.15, originY: 0.8 }}>
        <path
          d="M84.721 10.132c1.275-.475 2.678.38 2.926 1.783l.461 2.612c.518 2.934.036 5.94-1.361 8.495-1.398 2.555-3.623 4.498-6.289 5.492l-.502.187a38.951 38.951 0 013.13 8.798c.33-.462.69-.9 1.081-1.314 1.974-2.084 4.589-3.354 7.372-3.582l2.647-.218c1.314-.107 2.41 1.049 2.307 2.436l-.205 2.794c-.216 2.938-1.42 5.698-3.394 7.782-1.975 2.084-4.59 3.354-7.373 3.583L84 49.104a38.399 38.399 0 01-1.187 7.358c2.348-1.338 5.056-1.795 7.657-1.283l2.744.54c1.263.248 2.003 1.6 1.575 2.88l-.93 2.778c-.917 2.744-2.755 5.068-5.17 6.54-2.414 1.471-5.24 1.99-7.95 1.456l-2.744-.54a2.037 2.037 0 01-.908-.43 32.22 32.22 0 01-4.278 4.677 11.242 11.242 0 012.46 1.69l2.224 2.008c.95.859.95 2.35 0 3.21l-2.224 2.008a11.243 11.243 0 01-15.072 0c-1.399-1.263-2.721-2.585-2.732-4.534a32.018 32.018 0 0011.876-6.457c4.35-3.552 7.769-8.49 9.612-14.408a32.059 32.059 0 001.567-9.93c0-1.949-.174-3.856-.506-5.708-.738-4.547-2.344-8.748-4.475-12.411-2.121-3.09-1.091-9.092.521-12.04 1.398-2.555 3.623-4.498 6.289-5.491l2.373-.885z"
          fill={`url(#${uid}-laurel_right)`}
        />
      </motion.g>
      <motion.g variants={CENTER_VARIANTS} style={{ originX: 0.5, originY: 0.5 }}>
        <g filter={`url(#${uid}-inner_shadow)`}>
          <circle cx="48.5" cy="46.067" fill="#0A0A0A" r="29.268" />
          <circle
            cx="48.5"
            cy="46.067"
            fill={`url(#${uid}-circle_gradient)`}
            fillOpacity="0.2"
            r="29.268"
          />
        </g>
        <g filter={`url(#${uid}-drop_shadow)`}>
          <path
            d="M36.18 29.676c-1.23 0-2.388.58-3.125 1.564l-2.161 2.889c-1.97 2.632-1.287 6.26 1.527 8.104 2.813 1.843 6.691 1.204 8.661-1.429l.445-.594c.388-.518 1.245-.373 1.587.177 1.078 1.732 3.083 2.896 5.38 2.896 2.296 0 4.302-1.165 5.38-2.898.342-.55 1.2-.696 1.588-.177l.456.609c1.97 2.632 5.848 3.272 8.661 1.429 2.814-1.843 3.497-5.472 1.527-8.105l-2.17-2.9a3.903 3.903 0 00-3.126-1.565H36.18z"
            fill={`url(#${uid}-face_top)`}
          />
          <path
            clipRule="evenodd"
            d="M32.89 45.903v8.748a7.805 7.805 0 007.805 7.805h15.61a7.805 7.805 0 007.805-7.805v-8.736c-3.192 1.073-6.813.417-9.368-1.803a9.589 9.589 0 01-6.248 2.293 9.59 9.59 0 01-6.25-2.293c-2.555 2.21-6.168 2.861-9.354 1.79zm15.61 4.065a3.131 3.131 0 00-3.122 3.122v6.244h6.244V53.09a3.131 3.131 0 00-3.122-3.122z"
            fill={`url(#${uid}-face_bottom)`}
            fillRule="evenodd"
          />
        </g>
      </motion.g>
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="66.956"
          id={`${uid}-inner_shadow`}
          width="58.536"
          x="19.232"
          y="8.379"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset dy="-12.63" />
          <feGaussianBlur stdDeviation="4.21" />
          <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.9 0" />
          <feBlend in2="shape" mode="multiply" result="effect1_innerShadow" />
        </filter>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="40.667"
          id={`${uid}-drop_shadow`}
          width="45.35"
          x="27.797"
          y="29.676"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset dx="1.972" dy="3.943" />
          <feGaussianBlur stdDeviation="1.972" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.5 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
        </filter>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${uid}-laurel_left`}
          x1="19.627"
          x2="19.627"
          y1="-28.874"
          y2="82.213"
        >
          <stop offset="0.245" stopColor="#FBE646" />
          <stop offset="1" stopColor="#E39601" />
        </linearGradient>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${uid}-laurel_right`}
          x1="19.627"
          x2="19.627"
          y1="-28.874"
          y2="82.213"
        >
          <stop offset="0.245" stopColor="#FBE646" />
          <stop offset="1" stopColor="#E39601" />
        </linearGradient>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${uid}-circle_gradient`}
          x1="48.5"
          x2="48.5"
          y1="16.799"
          y2="75.335"
        >
          <stop stopColor="#E39601" />
          <stop offset="1" stopColor="#FEEF4E" />
        </linearGradient>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${uid}-face_top`}
          x1="48.5"
          x2="48.5"
          y1="14.484"
          y2="63.947"
        >
          <stop offset="0.45" stopColor="#FBE646" />
          <stop offset="1" stopColor="#E29500" />
        </linearGradient>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${uid}-face_bottom`}
          x1="48.5"
          x2="48.5"
          y1="14.484"
          y2="63.947"
        >
          <stop offset="0.45" stopColor="#FBE646" />
          <stop offset="1" stopColor="#E29500" />
        </linearGradient>
      </defs>
    </motion.svg>
  );
}
